import React from "react";
import DatePickerInput from "../../components/form/DatePickerInput";
import {
  questionUsesFreeText,
  selectionIncludesValue,
} from "./formSelection";

function buildDiscreteRangeValues(question) {
  const min = Number(question.min);
  const max = Number(question.max);
  const step = Number(question.step || 1);
  const values = [];
  const maxDiscreteOptions = 12;

  if (
    !Number.isFinite(min) ||
    !Number.isFinite(max) ||
    !Number.isFinite(step) ||
    step <= 0 ||
    max < min
  ) {
    return values;
  }

  for (
    let nextValue = min;
    nextValue <= max + step / 1000;
    nextValue += step
  ) {
    values.push(Number(nextValue.toFixed(10)));

    if (values.length > maxDiscreteOptions) {
      return [];
    }
  }

  return values;
}

/**
 * `renderInputField`
 * ------------------
 * Pure factory that returns the correct `<input>` / `<select>` element
 * for a questionnaire *question* definition.
 *
 * @param {Object}          params
 * @param {Object}          params.question     Full question object
 * @param {*}               params.value        Current answer for this question
 * @param {Function}        params.onChange     Shared `onChange` handler
 * @param {Function}        [params.onEnter]    Called when user presses Enter
 * @param {React.RefObject} params.inputRef     Ref so parent can focus the field
 *
 * @returns {JSX.Element}  A ready-to-render React element
 *
 * Supported `question.fieldType` values:
 *   • "text"   – default fallback
 *   • "number"
 *   • "date"
 *   • "select" – requires `question.options = [{ value, label }]`
 *   • "radio"  – requires `question.options = [{ value, label }]`
 *   • "checkbox-group" – multi-select outlined checkboxes
 *   • "episode-timing" – structured timing object with date/duration/ongoing
 *   • "range"  – requires `question.min`, `question.max`, optional `step`
 *
 *  This helper is **pure**: no React hooks, no side-effects.
 *  Safe to unit-test in isolation or move to a util module.
 */
export default function renderInputField({
  question,
  value,
  formData,
  onChange,
  onEnter,
  inputRef,
  t = (k) => k, // injected translator (defaults to identity)
}) {
  const title = t(question.titleKey ?? question.title);
  const description = question.descriptionKey
    ? t(question.descriptionKey)
    : question.description;
  const rangeDesc = question.rangeDescriptionKey
    ? t(question.rangeDescriptionKey)
    : (question.rangeDescription ?? description);

  const helperText = question.helperTextKey ? t(question.helperTextKey) : null;
  const descId = helperText ? `${question.id}__hint` : undefined;
  const freeTextActive = questionUsesFreeText(question, value);
  const exclusiveOptionValues = new Set(
    Array.isArray(question.exclusiveOptionValues) &&
    question.exclusiveOptionValues.length > 0
      ? question.exclusiveOptionValues.map((entry) => String(entry ?? ""))
      : ["none"],
  );

  // Base props shared by every field type -----------------------------
  // These ensure consistent styling, Enter-key behaviour, and ref wiring.
  const common = {
    id: question.id,
    name: question.id,
    placeholder: title,
    className: "custom-input w-full appearance-none",
    value: value ?? "",
    onChange,
    onKeyDown: (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        onEnter?.(e);
      }
    },
    required: question.required,
    ref: inputRef,
  };

  switch (question.fieldType) {
    case "number": {
      // ----- number -----
      const inputType = question.inputType ?? "number";
      return (
        <input
          type={inputType}
          {...common}
          min={inputType === "number" ? question.min : undefined}
          max={inputType === "number" ? question.max : undefined}
          step={inputType === "number" ? question.step : undefined}
          inputMode={question.inputMode ?? "decimal"}
        />
      );
    }
    case "date": {
      return (
        <DatePickerInput
          id={question.id}
          value={value}
          onChange={onChange}
          required={question.required}
          preferDateParts={question.id === "dateOfBirth"}
          className=""
        />
      );
    }
    case "select":
      // ----- select -----
      return (
        <div className="space-y-3">
          <select {...common}>
            <option value="">{title}</option>
            {question.options.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {t(opt.labelKey ?? opt.label)}
              </option>
            ))}
          </select>
          {freeTextActive ? (
            <div className="space-y-2">
              <label
                htmlFor={question.freeTextFieldId}
                className="subtitle-text"
              >
                {t(question.freeTextTitleKey ?? question.freeTextLabelKey)}
              </label>
              <input
                type="text"
                id={question.freeTextFieldId}
                name={question.freeTextFieldId}
                className="custom-input w-full appearance-none"
                value={formData?.[question.freeTextFieldId] ?? ""}
                onChange={onChange}
                onKeyDown={common.onKeyDown}
                required={question.required}
                placeholder={t(
                  question.freeTextPlaceholderKey ??
                    question.freeTextTitleKey ??
                    question.freeTextLabelKey ??
                    question.freeTextFieldId,
                )}
              />
            </div>
          ) : null}
        </div>
      );
    case "checkbox-group": {
      const selectedValues = Array.isArray(value) ? value : [];
      const handleToggle = (optionValue) => {
        const currentlySelected = selectionIncludesValue(
          selectedValues,
          optionValue,
        );
        const optionKey = String(optionValue ?? "");

        let nextSelectedValues;

        if (exclusiveOptionValues.has(optionKey)) {
          nextSelectedValues = currentlySelected ? [] : [optionValue];
        } else {
          const filteredExisting = selectedValues.filter(
            (entry) => !exclusiveOptionValues.has(String(entry ?? "")),
          );

          nextSelectedValues = currentlySelected
            ? filteredExisting.filter(
                (entry) =>
                  String(entry ?? "") !== optionKey,
              )
            : [...filteredExisting, optionValue];
        }

        onChange({
          target: {
            name: question.id,
            value: nextSelectedValues,
          },
        });
      };

      return (
        <div className="space-y-3">
          {helperText && (
            <p id={descId} className="muted-text">
              {helperText}
            </p>
          )}
          <fieldset className="space-y-2" id={question.id}>
            {question.options.map((opt, index) => {
              const checked = selectionIncludesValue(selectedValues, opt.value);

              return (
                <label key={opt.value} className="radio-outlined-button">
                  <input
                    type="checkbox"
                    name={question.id}
                    value={opt.value}
                    checked={checked}
                    onChange={() => handleToggle(opt.value)}
                    required={question.required && selectedValues.length === 0}
                    aria-describedby={descId}
                    ref={index === 0 ? inputRef : undefined}
                    className="choice-circle-input"
                  />
                  <span className="text-base">
                    {t(opt.labelKey ?? opt.label)}
                  </span>
                </label>
              );
            })}
          </fieldset>

          {freeTextActive ? (
            <div className="space-y-2">
              <label
                htmlFor={question.freeTextFieldId}
                className="subtitle-text"
              >
                {t(question.freeTextTitleKey ?? question.freeTextLabelKey)}
              </label>
              <input
                type="text"
                id={question.freeTextFieldId}
                name={question.freeTextFieldId}
                className="custom-input w-full appearance-none"
                value={formData?.[question.freeTextFieldId] ?? ""}
                onChange={onChange}
                onKeyDown={common.onKeyDown}
                required={question.required}
                placeholder={t(
                  question.freeTextPlaceholderKey ??
                    question.freeTextTitleKey ??
                    question.freeTextLabelKey ??
                    question.freeTextFieldId,
                )}
              />
            </div>
          ) : null}
        </div>
      );
    }
    case "episode-timing": {
      const timingFields =
        question.timingFields && typeof question.timingFields === "object"
          ? question.timingFields
          : {};
      const timingValue = value && typeof value === "object" ? value : {};
      const unknown = timingValue.unknown === true;
      const setTimingValue = (nextPatch) => {
        onChange({
          target: {
            name: question.id,
            value: {
              ...timingValue,
              ...nextPatch,
            },
          },
        });
      };

      return (
        <div className="space-y-4">
          <label className="radio-outlined-button">
            <input
              type="checkbox"
              checked={unknown}
              onChange={(event) =>
                setTimingValue(
                  event.target.checked
                    ? {
                        unknown: true,
                        approximateOnsetDate: "",
                        durationDays: "",
                        ongoing: null,
                      }
                    : {
                        unknown: false,
                      },
                )
              }
              ref={inputRef}
              className="choice-circle-input"
            />
            <span className="text-base">
              {t(
                timingFields.unknownLabelKey ??
                  timingFields.unknownLabel ??
                  "I can't remember these details",
              )}
            </span>
          </label>

          {!unknown ? (
            <div className="space-y-4">
              <div className="space-y-2">
                <label
                  htmlFor={`${question.id}__approximateOnsetDate`}
                  className="subtitle-text"
                >
                  {t(
                    timingFields.onsetDateLabelKey ??
                      timingFields.onsetDateLabel ??
                      "Approximate start date",
                  )}
                </label>
                <DatePickerInput
                  id={`${question.id}__approximateOnsetDate`}
                  value={timingValue.approximateOnsetDate ?? ""}
                  onChange={(event) =>
                    setTimingValue({
                      approximateOnsetDate: event.target.value,
                    })
                  }
                  required={question.required}
                />
              </div>

              <div className="space-y-2">
                <label
                  htmlFor={`${question.id}__durationDays`}
                  className="subtitle-text"
                >
                  {t(
                    timingFields.durationDaysLabelKey ??
                      timingFields.durationDaysLabel ??
                      "Duration in days",
                  )}
                </label>
                <input
                  id={`${question.id}__durationDays`}
                  name={`${question.id}__durationDays`}
                  type="number"
                  min={1}
                  step={1}
                  inputMode="numeric"
                  className="custom-input w-full appearance-none"
                  value={timingValue.durationDays ?? ""}
                  onChange={(event) =>
                    setTimingValue({
                      durationDays: event.target.value,
                    })
                  }
                  onKeyDown={common.onKeyDown}
                  required={question.required}
                />
              </div>

              <fieldset className="space-y-2">
                <legend className="subtitle-text">
                  {t(
                    timingFields.ongoingLabelKey ??
                      timingFields.ongoingLabel ??
                      "Is it still ongoing?",
                  )}
                </legend>
                {(timingFields.ongoingOptions || []).map((option) => {
                  const checked = timingValue.ongoing === option.value;

                  return (
                    <label
                      key={String(option.value)}
                      className="radio-outlined-button"
                    >
                      <input
                        type="radio"
                        name={`${question.id}__ongoing`}
                        value={String(option.value)}
                        checked={checked}
                        onChange={() =>
                          setTimingValue({
                            ongoing: option.value,
                          })
                        }
                        required={question.required}
                        className="accent-primary-600"
                        style={{ transform: "scale(1.35)" }}
                      />
                      <span className="text-base">
                        {t(option.labelKey ?? option.label)}
                      </span>
                    </label>
                  );
                })}
              </fieldset>
            </div>
          ) : null}
        </div>
      );
    }
    case "range":
      // ----- range -----
      if (question.rangePresentation === "choice-row") {
        const discreteValues = buildDiscreteRangeValues(question);

        if (discreteValues.length > 0) {
          return (
            <div className="space-y-3">
              {helperText && (
                <p id={descId} className="muted-text">
                  {helperText}
                </p>
              )}

              <fieldset className="space-y-3" id={question.id}>
                <div
                  className="grid w-full min-w-0 gap-1.5"
                  style={{
                    gridTemplateColumns: `repeat(${discreteValues.length}, minmax(0, 1fr))`,
                  }}
                >
                  {discreteValues.map((optionValue, index) => {
                    const optionValueText = String(optionValue);
                    const checked = String(value ?? question.min) === optionValueText;

                    return (
                      <label
                        key={optionValueText}
                        className={`flex min-w-0 cursor-pointer flex-col items-center justify-center gap-1 rounded-xl border-2 px-1 py-2 text-center transition-colors ${
                          checked
                            ? "border-primary bg-primary/5"
                            : "border-greyscale-1 bg-transparent"
                        }`}
                      >
                        <span
                          className={`text-xs font-semibold leading-none ${
                            checked ? "text-primary" : "text-greyscale-3"
                          }`}
                        >
                          {optionValueText}
                        </span>
                        <input
                          type="radio"
                          name={question.id}
                          value={optionValueText}
                          checked={checked}
                          onChange={() =>
                            onChange({
                              target: {
                                name: question.id,
                                value: optionValueText,
                              },
                            })
                          }
                          required={question.required}
                          aria-describedby={descId}
                          ref={index === 0 ? inputRef : undefined}
                          className="choice-circle-input"
                        />
                      </label>
                    );
                  })}
                </div>

                {rangeDesc && <p className="muted-text whitespace-pre-line">{rangeDesc}</p>}
              </fieldset>
            </div>
          );
        }
      }

      return (
        <div className="range-input-container">
          <input
            type="range"
            {...common}
            className="custom-range w-full"
            value={value ?? question.min}
            min={question.min}
            max={question.max}
            step={question.step || 1}
          />
          <div className="range-values text-xs flex justify-between">
            <span>{question.min}</span>
            <span className="subtitle-text">{value ?? question.min}</span>
            <span>{question.max}</span>
          </div>
          {rangeDesc && <p className="muted-text">{rangeDesc}</p>}
        </div>
      );

    case "radio":
      return (
        <fieldset className="space-y-2" id={question.id}>
          {helperText && (
            <p id={descId} className="muted-text">
              {helperText}
            </p>
          )}

          {question.options.map((opt, index) => {
            const val = String(opt.value);
            const checked = String(value ?? "") === val;

            return (
              <label key={val} className="radio-outlined-button">
                <input
                  type="radio"
                  name={question.id}
                  value={val}
                  checked={checked}
                  onChange={() =>
                    onChange({
                      target: { name: question.id, value: opt.value },
                    })
                  }
                  required={question.required}
                  aria-describedby={descId}
                  ref={index === 0 ? inputRef : undefined}
                  // enlarge the glyph; label provides the 44px target
                  style={{ transform: "scale(1.35)" }}
                  className="accent-primary-600"
                />
                <span className="text-base">
                  {t(opt.labelKey ?? opt.label)}
                </span>
              </label>
            );
          })}

          {rangeDesc && <p className="muted-text">{rangeDesc}</p>}
          {Array.isArray(question.legendList) &&
            question.legendList.length > 0 && (
              <ul className="text-xs mt-2 list-disc pl-5">
                {question.legendList.map((k) => (
                  <li key={k}>{t(k)}</li>
                ))}
              </ul>
            )}
        </fieldset>
      );
    case "boolean-matrix": {
      const checked = (rowId, bool) => (value ?? {})[rowId] === bool;
      const handle = (rowId, bool) => {
        const cur = value ?? {};
        onChange({
          target: { name: question.id, value: { ...cur, [rowId]: bool } },
        });
      };

      return (
        <div className="boolean-matrix overflow-x-auto">
          {helperText && (
            <p id={descId} className="muted-text mb-2">
              {helperText}
            </p>
          )}

          <table className="w-full text-base">
            <thead>
              <tr>
                <th className="text-left py-3 pr-2"></th>
                {question.columns.map((c, idx) => (
                  <th
                    key={String(c.value)}
                    id={`${question.id}__col_${idx}`}
                    className="py-3 px-2"
                  >
                    {t(c.labelKey ?? c.label)}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {question.rows.map((r) => (
                <tr key={r.id} className="border-t">
                  <td
                    id={`${question.id}__row_${r.id}`}
                    className="py-3 pr-2 align-top"
                  >
                    {t(r.labelKey ?? r.label)}
                  </td>

                  {question.columns.map((c, idx) => {
                    const isChecked = checked(r.id, c.value);
                    return (
                      <td key={String(c.value)} className="py-1 px-1">
                        <label className="radio-cell">
                          <input
                            type="radio"
                            name={`${question.id}__${r.id}`}
                            checked={isChecked}
                            onChange={() => handle(r.id, c.value)}
                            required={question.required}
                            aria-labelledby={`${question.id}__row_${r.id} ${question.id}__col_${idx}`}
                          />
                        </label>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    case "textarea":
      return (
        <textarea
          {...common}
          rows={4}
          placeholder={t(
            question.placeholderKey ??
              question.placeholder ??
              question.titleKey ??
              question.title,
          )}
        />
      );
    default:
      // ----- text (default) -----
      return <input type="text" {...common} />;
  }
}
